/**
 * Единственное место, где решается «показывать ли паспортные поля».
 *
 * Раньше маскирование жило внутри `services/candidates.ts` и было навешено
 * ровно на два вызова — список и карточку. Тех же данных касались ещё три
 * потребителя (WebSocket-рассылка, лента изменений, выгрузка), и ни один
 * до маскирования не доходил: у рассылки нет получателя в момент сборки
 * сообщения, у ленты не было актора в сигнатуре, а выгрузка роль не смотрела
 * вовсе. Дырой это стало не потому, что забыли строчку, а потому, что
 * преобразование было привязано к двум точкам вызова вместо данных.
 *
 * Отсюда два правила этого модуля:
 *
 * 1. Список полей — один на весь монорепозиторий (`DOCUMENT_ONLY_COLUMNS`).
 *    Копий было три, и одна из них, в тесте, уже разошлась: в ней не было
 *    `passportIssueDate`, то есть страховка на это поле не срабатывала.
 *
 * 2. Проверка fail-closed: актора нет — маскируем. Прежняя проверка была
 *    устроена наоборот (`if (!actor || hasPermission(...)) return items`),
 *    и любой путь, забывший прокинуть роль, молча отдавал паспорт целиком.
 *    Ошибиться в сторону «спрятали лишнее» здесь дёшево, в обратную — нет.
 */

import { DOCUMENT_ONLY_COLUMNS } from '../constants/columns';
import { hasPermission, type Role } from '../constants/rbac';

/** Минимум, который нужно знать о пользователе, чтобы решить видимость. */
export interface DocumentActor {
  role: Role;
}

const DOCUMENT_FIELD_SET: ReadonlySet<string> = new Set(DOCUMENT_ONLY_COLUMNS);

/** Является ли колонка паспортной. */
export function isDocumentField(field: string): boolean {
  return DOCUMENT_FIELD_SET.has(field);
}

/**
 * Видит ли актор паспортные поля.
 *
 * `null`/`undefined` — это не «системный вызов», а «роль сюда не дотянули».
 * Такой вызов получает отказ.
 */
export function canViewDocuments(actor: DocumentActor | null | undefined): boolean {
  return actor ? hasPermission(actor.role, 'document.view') : false;
}

/**
 * КОПИЯ строки с обнулёнными паспортными полями.
 *
 * Именно копия, а не правка на месте: то же сообщение WebSocket уходит
 * нескольким получателям с разными ролями, и обнуление общего объекта
 * ради одного наблюдателя стёрло бы значения и у рекрутёров.
 */
export function maskDocumentRow<T extends Record<string, unknown>>(row: T): T {
  const out: Record<string, unknown> = { ...row };
  for (const field of DOCUMENT_ONLY_COLUMNS) {
    if (field in out) out[field] = null;
  }

  /*
    Заливка паспортных ячеек уезжает вместе со значениями. Колонок этих
    наблюдатель не видит вовсе, и «в номере паспорта что-то отмечено
    красным» — тоже сведение о данных, которых ему не показывают.
  */
  for (const key of ['cellColors', 'cellBold']) {
    const styles = out[key];
    if (!styles || typeof styles !== 'object') continue;
    const kept: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(styles)) {
      if (!DOCUMENT_FIELD_SET.has(field)) kept[field] = value;
    }
    out[key] = kept;
  }

  return out as T;
}

/** Строки под конкретного актора. Право есть — массив возвращается как есть. */
export function maskDocumentRows<T extends Record<string, unknown>>(
  rows: T[],
  actor: DocumentActor | null | undefined,
): T[] {
  if (canViewDocuments(actor)) return rows;
  return rows.map(maskDocumentRow);
}

/**
 * Значение одного поля под актора: `null`, если поле паспортное, а права нет.
 *
 * Нужно там, где едет не строка, а отдельная ячейка — событие `cell.changed`
 * и записи журнала изменений.
 */
export function maskDocumentValue<V>(
  field: string,
  value: V,
  actor: DocumentActor | null | undefined,
): V | null {
  if (!isDocumentField(field)) return value;
  return canViewDocuments(actor) ? value : null;
}

/** Отбор колонок, которые актору можно отдать: для выгрузки и копирования. */
export function allowedDocumentColumns(
  columns: readonly string[],
  actor: DocumentActor | null | undefined,
): string[] {
  if (canViewDocuments(actor)) return [...columns];
  return columns.filter((c) => !isDocumentField(c));
}
